import {Paragraph} from "@/pages/common/Home/Dissection/Article";

import article_4 from "@images/home/analytics/beyond_reach/article-4.png";
import gen_z_hcp_icon_1 from "@images/home/analytics/beyond_reach/article_4_gen_z-1.png";
import gen_z_hcp_icon_2 from "@images/home/analytics/beyond_reach/article_4_gen_z-2.png";
import gen_z_hcp_icon_3 from "@images/home/analytics/beyond_reach/article_4_gen_z-3.png";
import gen_z_hcp from "@images/home/analytics/beyond_reach/article_4_gen_z-hcp.png";
import triple_32 from "@images/home/analytics/beyond_reach/article_4_triple-32per.svg";
import triple_39 from "@images/home/analytics/beyond_reach/article_4_triple-39per.svg";
import triple_53 from "@images/home/analytics/beyond_reach/article_4_triple-53per.svg";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {H2} from "@ui/titles/H2";
import {H3} from "@ui/titles/H3";
import {useSelector} from "react-redux";

import styles from "../styles.module.scss";

export const ArticleBeyondReach = () => {
  const {isMobile} = useSelector((state) => state.common.settings);

  return (
    <>
      <Paragraph>
        <H2>
          <span>
            Beyond Reach: Enabling the&nbsp;New&nbsp;Era of&nbsp;Digital-First
            Pharma
          </span>
        </H2>
      </Paragraph>

      <Paragraph>
        <div
          className={styles.article_text_image}
          style={{flexDirection: isMobile ? "column-reverse" : "row-reverse"}}
        >
          <div>
            <div className={styles.article_text_card}>
              <ul className={styles.article_top_priority_list}>
                <li>
                  <RootDesc>
                    <span className={styles.article_gradient_text}>
                      Gen Alpha is&nbsp;the&nbsp;first generation born into
                      a&nbsp;world where AI&nbsp;isn't new&nbsp;-
                      it’s&nbsp;native
                    </span>
                  </RootDesc>
                </li>
              </ul>
            </div>
          </div>

          <div style={{paddingLeft: isMobile ? "0" : "120rem"}}>
            <RootDesc>
              <span>
                Can you remember the&nbsp;first generation that was born using
                the internet? That&nbsp;shift redefined how we communicate,
                learn, and&nbsp;live. Now&nbsp;consider this: Gen Alpha
                is&nbsp;the&nbsp;first generation born into a&nbsp;world
                where&nbsp;AI isn't new&nbsp;- it’s native. Let that
                sink&nbsp;in.
              </span>

              <span>
                Just as&nbsp;the&nbsp;internet reshaped how industries operated,
                AI&nbsp;is&nbsp;now redefining how we engage, inform,
                and&nbsp;enable&nbsp;- both internally and&nbsp;externally.
                For&nbsp;pharma, this means that the&nbsp; people we hire
                and&nbsp;the&nbsp;professionals we serve are&nbsp;changing.
                Dramatically. And&nbsp;permanently.
              </span>
            </RootDesc>
          </div>
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Generation&nbsp;Z is&nbsp;becoming the&nbsp;third-largest
              generation in&nbsp;the&nbsp;UK workforce, with over
              4.3&nbsp;million employees<sup>13</sup>. According
              to&nbsp;Glassdoor, 2024 was most likely the&nbsp;first year
              Generation&nbsp;Z surpassed Baby Boomers
              in&nbsp;the&nbsp;workforce<sup>14</sup>.
            </span>

            <span>
              Pharma is no&nbsp;exception. Pharmaceutical companies are facing
              a&nbsp;generational shift&nbsp;- both within their field teams and
              among their customers. On&nbsp;one side, Gen&nbsp;Z
              and&nbsp;younger millennials are entering commercial roles with
              high expectations for digital tools, real-time feedback, and
              purpose-driven engagement. On&nbsp;the other, early-career
              healthcare professionals (HCPs) are increasingly digital-native
              themselves, favouring peer-led learning, remote engagement, and
              scientifically credible content over traditional detailing.
            </span>
          </RootDesc>

          <article style={{marginTop: "26rem", marginBottom: "26rem"}}>
            <img
              src={article_4}
              alt={"image"}
            />
          </article>

          <RootDesc>
            <span>
              In&nbsp;this new reality, it’s no&nbsp;longer about convincing
              reps to&nbsp;use CRM systems or adopt hybrid communication formats
              - they expect it. Today’s field teams aren’t just expected
              to&nbsp;be digitally savvy; they demand that the tools and
              solutions provided to&nbsp;them match the pace and standards
              they’re used to.
            </span>

            <span>
              Interestingly, according to&nbsp;the&nbsp;latest IQVIA
              ChannelDynamics<sup>TM</sup> 2024 study, channel alignment between
              preferred and actual HCP engagement in&nbsp;the&nbsp;UK stands
              at&nbsp;only 38%. This highlights a&nbsp;substantial gap between
              HCP expectations and the reality of&nbsp;omnichannel execution
              across local affiliates<sup>15</sup>.
            </span>

            <span>
              Along with the dual dynamic (digitally fluent field teams and
              digitally demanding HCPs) it is forcing pharma to&nbsp;rethink
              what commercial excellence really looks like. It’s no&nbsp;longer
              enough to&nbsp;optimise for reach or&nbsp;frequency. The next
              generation of&nbsp;reps and&nbsp;HCPs expect technology-enabled,
              personalised, and&nbsp;contextually relevant engagement&nbsp;-
              both inside and&nbsp;outside the organisation.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>The Evolving Landscape of&nbsp;Commercial Excellence</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <i className={styles.article_label}>
              <i>
                <span>1</span>
              </i>

              <div>
                <H3>
                  <span style={{fontSize: "28rem"}}>
                    HCP / Marketing Perspective
                  </span>
                </H3>
              </div>
            </i>

            <H3>
              <span>
                Missing a&nbsp;Strategic Audience: Gen&nbsp;Z&nbsp;HCPs
              </span>
            </H3>

            <span>
              Many companies still prioritise traditional&nbsp;KOLs.
              Yet&nbsp;Gen&nbsp;Z&nbsp;HCPs are&nbsp;4x more likely
              to&nbsp;adopt new treatments, more scientifically active,
              and&nbsp;more likely to&nbsp;influence peers digitally. Engaging
              them early is&nbsp;a&nbsp;strategic advantage<sup>8,7</sup>.
            </span>

            <div className={styles.beyond_reach_image_text}>
              <img
                className={styles.beyond_reach_image_text_image}
                src={gen_z_hcp}
                alt={"image"}
              />

              <div className={styles.beyond_reach_image_text_content}>
                <RootDesc>
                  <b
                    className={
                      "text-[28rem] leading-[120%] text-[var(--white)]"
                    }
                  >
                    Gen Z HCPs are{" "}
                    <strong className={"text-[var(--violet)]"}>4x</strong> more
                    likely:
                  </b>
                </RootDesc>

                <ul style={{listStyle: "none"}}>
                  <li style={{listStyle: "none"}}>
                    <img
                      src={gen_z_hcp_icon_1}
                      alt={"icon"}
                    />

                    <RootDesc>
                      <b className={"text-[var(--white)]"}>
                        adopt new treaments
                      </b>
                    </RootDesc>
                  </li>

                  <li style={{listStyle: "none"}}>
                    <img
                      src={gen_z_hcp_icon_2}
                      alt={"icon"}
                    />

                    <RootDesc>
                      <b className={"text-[var(--white)]"}>
                        scientifically active
                      </b>
                    </RootDesc>
                  </li>

                  <li style={{listStyle: "none"}}>
                    <img
                      src={gen_z_hcp_icon_3}
                      alt={"icon"}
                    />

                    <RootDesc>
                      <b className={"text-[var(--white)]"}>
                        influence peers digitally
                      </b>
                    </RootDesc>
                  </li>
                </ul>
              </div>
            </div>

            <H3>
              <span>
                Omnichannel Integration Is&nbsp;No&nbsp;Longer Optional
              </span>
            </H3>

            <span>
              Reps and marketers must coordinate&nbsp;HCP touchpoints across
              digital, face-to-face, and&nbsp;remote channels. This requires
              integrated data, cross-functional collaboration, and&nbsp;content
              designed to&nbsp;serve both rep-&nbsp;and&nbsp;AI-led interactions
              <sup>10,2</sup>.
            </span>

            <H3>
              <span>AI-Powered Content and&nbsp;Journey Personalisation</span>
            </H3>

            <span>
              Digital-native HCPs (especially early-career professionals) expect
              tailored, high-quality, science-first interactions. AI&nbsp;tools
              personalise content journeys, adapt messaging to&nbsp;behavioural
              insights, and&nbsp;optimise delivery timing across channels
              <sup>5,2</sup>.
            </span>

            <H3>
              <span>Marketing’s Role: Adaptive Brand Execution</span>
            </H3>

            <span>
              Marketing must shift from static assets to&nbsp;modular content
              ecosystems&nbsp;- allowing fast localisation, personalisation,
              and&nbsp;cross-channel deployment. This ensures brand consistency
              while enabling agility across markets<sup>3,11,9</sup>.
            </span>

            <i className={styles.article_label}>
              <i>
                <span>2</span>
              </i>

              <div>
                <H3>
                  <span style={{fontSize: "28rem"}}>
                    Field Force Perspective
                  </span>
                </H3>
              </div>
            </i>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "16rem",
                marginTop: "16rem",
              }}
              className={"phone:!grid-cols-1"}
            >
              <div
                className={
                  "flex flex-col gap-[16rem] rounded-[40rem] p-[24rem]"
                }
                style={{
                  background: "var(--violetGradient)",
                }}
              >
                <RootDesc>
                  <b
                    className={
                      "text-[23rem] leading-[120%] text-[var(--white)]"
                    }
                  >
                    From Static&nbsp;CLM to&nbsp;Smart Orchestration
                  </b>
                </RootDesc>

                <RootDesc>
                  <span className={"text-[var(--white)]"}>
                    CLM is&nbsp;evolving into an&nbsp;intelligent layer
                    of&nbsp;omnichannel engagement. Smart&nbsp;CLM surfaces
                    best-performing content, adapts presentations
                    to&nbsp;the&nbsp;HCP profile, and&nbsp;feeds back data
                    to&nbsp;improve campaign impact across channels
                    <sup>6,3,12</sup>.
                  </span>
                </RootDesc>
              </div>

              <div
                className={
                  "flex flex-col gap-[16rem] rounded-[40rem] p-[24rem]"
                }
                style={{
                  background: "var(--violetGradient)",
                }}
              >
                <RootDesc>
                  <b
                    className={
                      "text-[23rem] leading-[120%] text-[var(--white)]"
                    }
                  >
                    Immersive, On-Demand Enablement
                  </b>
                </RootDesc>

                <RootDesc>
                  <span className={"text-[var(--white)]"}>
                    Digital-native reps don’t want static training decks&nbsp;-
                    they expect interactive, mobile-first tools like AI-driven
                    simulations, adaptive content, and&nbsp;scenario-based
                    performance support that mirror the&nbsp;pressures
                    of&nbsp;real-world&nbsp;HCP engagement<sup>1,2</sup>.
                  </span>
                </RootDesc>
              </div>

              <div
                className={
                  "flex flex-col gap-[16rem] rounded-[40rem] p-[24rem]"
                }
                style={{
                  background: "var(--violetGradient)",
                }}
              >
                <RootDesc>
                  <b
                    className={
                      "text-[23rem] leading-[120%] text-[var(--white)]"
                    }
                  >
                    Enablement in&nbsp;the&nbsp;Flow of&nbsp;Work
                  </b>
                </RootDesc>

                <RootDesc>
                  <span className={"text-[var(--white)]"}>
                    Younger field teams expect enablement embedded in&nbsp;their
                    daily tools&nbsp;- delivered as&nbsp;micro-content, product
                    refreshers, or&nbsp;objection handling tips
                    in&nbsp;the&nbsp;CRM or&nbsp;CLM. This shift demands
                    agility, not&nbsp;just compliance<sup>3,4</sup>.
                  </span>
                </RootDesc>
              </div>

              <div
                className={
                  "flex flex-col gap-[16rem] rounded-[40rem] p-[24rem]"
                }
                style={{
                  background: "var(--violetGradient)",
                }}
              >
                <RootDesc>
                  <b
                    className={
                      "text-[23rem] leading-[120%] text-[var(--white)]"
                    }
                  >
                    Commercial Coaching at&nbsp;Scale
                  </b>
                </RootDesc>

                <RootDesc>
                  <span className={"text-[var(--white)]"}>
                    Decentralised teams need scalable support. Virtual coaching,
                    AI-powered mentors, and&nbsp;real-time analytics reduce
                    manager burden while ensuring alignment with brand strategy
                    and&nbsp;local nuances<sup>4,9</sup>.
                  </span>
                </RootDesc>
              </div>
            </div>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>
            The Opportunity: Commercial Excellence
            for&nbsp;a&nbsp;New&nbsp;Generation
          </span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Whilst acknowledging this shift, we need to remember that for now,
              the future of commercial excellence in pharma isn’t just
              digital&nbsp;- it’s human&nbsp;+ digital by design. While&nbsp;AI
              and automation are reshaping engagement models, humans will remain
              central to&nbsp;decision-making&nbsp;- especially
              in&nbsp;an&nbsp;industry governed by&nbsp;regulation, compliance,
              and&nbsp;trust.
            </span>

            <span>
              At the same time, the&nbsp;accelerating pace
              of&nbsp;digitalisation demands faster responses, sharper insights,
              and&nbsp;more adaptive execution. Here, AI&nbsp;plays
              a&nbsp;crucial role: helping teams keep up with change, scale
              personalised engagement, and&nbsp;translate strategy into action
              at&nbsp;the&nbsp;speed today’s market requires.
            </span>

            <span>
              Yet, it&nbsp;would seem training personalisation
              is&nbsp;not&nbsp;being considered. SalesFuel has&nbsp;found that
              only&nbsp;30% of&nbsp;pharma reps get personalised coaching
              <sup>16</sup>.
            </span>

            <span>
              However, companies that implement fit-for-purpose coaching can
              achieve an&nbsp;ROI of&nbsp;545%, with&nbsp;53% increased
              productivity, 39%&nbsp;improved customer service, and&nbsp;32%
              improved senior staff retention<sup>17</sup>.
            </span>
          </RootDesc>

          <div className={styles.article_beyond_roi}>
            <RootDesc>
              <b className={"text-[28rem] leading-[120%]"}>
                Companies that implement fit-for-purpose
                <br /> coaching can achieve an ROI of 545%, with...
              </b>
            </RootDesc>

            <div
              style={{display: "grid"}}
              className={styles.article_beyond_roi_cards}
            >
              <div className={styles.article_beyond_roi_card}>
                <img
                  src={triple_53}
                  alt={"53%"}
                />

                <RootDesc>
                  <b>Increase productivity</b>
                </RootDesc>
              </div>

              <div className={styles.article_beyond_roi_card}>
                <img
                  src={triple_39}
                  alt={"39%"}
                />

                <RootDesc>
                  <b>
                    Improve customer
                    <br /> service
                  </b>
                </RootDesc>
              </div>

              <div className={styles.article_beyond_roi_card}>
                <img
                  src={triple_32}
                  alt={"32%"}
                />

                <RootDesc>
                  <b>
                    Improve senior staff
                    <br /> retention
                  </b>
                </RootDesc>
              </div>
            </div>
          </div>
        </div>

        <RootDesc>
          <div className={styles.article_subtitle}>
            <span>
              This gap in&nbsp;personalised coaching is&nbsp;exactly where tools
              like&nbsp;AI Trainers make a&nbsp;difference. For example,
              AVA&nbsp;AI&nbsp;trainer<sup>18</sup> provides personalised
              coaching and&nbsp;practice exactly when it’s&nbsp;needed, adapting
              to&nbsp;the&nbsp;pace of&nbsp;each&nbsp;rep. Crucially,
              it&nbsp;acts as&nbsp;a&nbsp;bridge between marketing
              and&nbsp;commercial teams. Updated brand strategies, new content
              modules, or&nbsp;revised messaging can&nbsp;be&nbsp;delivered
              instantly, with&nbsp;reps practising the&nbsp;changes before
              taking them&nbsp;into the&nbsp;field. This&nbsp;creates
              consistency in&nbsp;omnichannel execution and&nbsp;accelerates
              adaptive brand rollouts.
            </span>

            <span>
              For the field force, the&nbsp;experience is&nbsp;immersive
              and&nbsp;practical. Reps can&nbsp;rehearse realistic&nbsp;HCP
              conversations, strengthen product fluency, and&nbsp;even overcome
              psychological barriers by&nbsp;engaging with different simulated
              client types&nbsp;- from local doctors to&nbsp;international
              opinion leaders. Meanwhile, managers gain visibility through
              learning analytics, spotting common gaps and&nbsp;organising
              targeted coaching or&nbsp;group sessions where needed.
            </span>

            <span>
              Companies that meet the&nbsp;expectations of&nbsp;both digitally
              native reps and&nbsp;digital-first HCPs will be&nbsp;best
              positioned to&nbsp;drive engagement, accelerate uptake,
              and&nbsp;build long-term trust.
            </span>
          </div>
        </RootDesc>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Where Pharma Goes Next</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Pharma can no&nbsp;longer afford to&nbsp;treat digital
              as&nbsp;a&nbsp;channel or&nbsp;training
              as&nbsp;an&nbsp;afterthought. As&nbsp;both field teams
              and&nbsp;HCPs become younger and&nbsp;more tech-savvy, commercial
              and&nbsp;marketing strategies must evolve. This&nbsp;means
              equipping reps with intelligent tools, orchestrating personalised
              omnichannel journeys, and&nbsp;recognising that&nbsp;overlooking
              digital-native HCPs is&nbsp;a&nbsp;missed strategic opportunity.
            </span>

            <span>
              The&nbsp;companies that act now&nbsp;(those investing
              in&nbsp;modern, modular solutions for&nbsp;commercial enablement)
              won’t just keep up with the&nbsp;future. They’ll define&nbsp;it.
            </span>

            <span>
              The future of&nbsp;pharma won’t be&nbsp;just digital&nbsp;-
              it&nbsp;will be&nbsp;fluent. And&nbsp;fluency must be&nbsp;taught.
            </span>

            <span>
              Start by asking: are&nbsp;your reps truly prepared for
              digital-first, data-driven conversations? Is&nbsp;your training
              continuous, adaptive, and&nbsp;personalised to&nbsp;their needs?
              The&nbsp;technology is&nbsp;here. What matters now is&nbsp;putting
              it&nbsp;to&nbsp;work.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_citations}>
          <h6>References</h6>

          <RootDesc>
            <ol>
              <li>
                <span>
                  EI&nbsp;Design. L&D&nbsp;Trends&nbsp;2025. eidesign.net.
                </span>
              </li>

              <li>
                <span>
                  Deloitte. Global Gen&nbsp;Z and&nbsp;Millennial
                  Survey&nbsp;2025.{" "}
                  <a
                    href={"https://deloitte.com"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    deloitte.com.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  TalentLMS. 11&nbsp;L&D Trends for&nbsp;2025 &&nbsp;Beyond.{" "}
                  <a
                    href={"https://talentlms.com"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    talentlms.com.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  Continu. Corporate eLearning Statistics&nbsp;2025.{" "}
                  <a
                    href={"https://continu.com"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    continu.com.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  McKinsey. AI in the&nbsp;Workplace: A&nbsp;Report
                  for&nbsp;2025.{" "}
                  <a
                    href={"https://mckinsey.com"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    mckinsey.com.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  Articulate. eLearning Trends for&nbsp;2025.{" "}
                  <a
                    href={"https://articulate.com"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    articulate.com.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  ADVANZ Pharma. Global Medical Excellence &&nbsp;Operations
                  Statement.&nbsp;2024.
                </span>
              </li>

              <li>
                <span>Veeva. Pulse Field Trends Report.&nbsp;2024.</span>
              </li>

              <li>
                <span>
                  Deloitte&nbsp;AU. GenAI Training and&nbsp;Learning Trends.{" "}
                  <a
                    href={"https://theaustralian.com.au"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    theaustralian.com.au.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  McKinsey. Next-generation Engagement
                  in&nbsp;Pharma.&nbsp;2024.{" "}
                  <a
                    href={"https://mckinsey.com"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    mckinsey.com.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  Deloitte Insights. Pharma Marketing&nbsp;2025: Reimagining
                  the&nbsp;Content Lifecycle.&nbsp;2024.{" "}
                  <a
                    href={"https://deloitte.com"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    deloitte.com.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  Veeva. How&nbsp;Intelligent CLM is&nbsp;Transforming Field
                  Engagement.&nbsp;2023.{" "}
                  <a
                    href={"https://veeva.com"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    Veeva.com
                  </a>
                </span>
              </li>

              <li>
                <a
                  href={
                    "https://acaciumgroup.com/case-studies/gen-zs-influence-on-nhs-workforce-dynamics/"
                  }
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  https://acaciumgroup.com/case-studies/gen-zs-influence-on-nhs-workforce-dynamics/.
                </a>
              </li>

              <li>
                <a
                  href={
                    "https://www.emjreviews.com/emj-gold/article/get-ready-for-gen-z-doctors/"
                  }
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  https://www.emjreviews.com/emj-gold/article/get-ready-for-gen-z-doctors/.
                </a>
              </li>

              <li>
                <a
                  href={
                    "https://www.iqvia.com/locations/emea/blogs/2024/11/understanding-healthcare-professionals-changing-channel-preferences-to-build-better-engagement"
                  }
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  https://www.iqvia.com/locations/emea/blogs/2024/11/understanding-healthcare-professionals-changing-channel-preferences-to-build-better-engagement.
                </a>
              </li>

              <li>
                <a
                  href={
                    "https://salesfuel.com/whats-the-difference-between-sales-managers-and-sales-representatives/"
                  }
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  https://salesfuel.com/whats-the-difference-between-sales-managers-and-sales-representatives/.
                </a>
              </li>

              <li>
                <span>
                  <a
                    href={
                      "https://exeevo.com/resources/publications/blog/field-force-effectiveness-pharma/"
                    }
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    https://exeevo.com/resources/publications/blog/field-force-effectiveness-pharma/
                  </a>{" "}
                  (THE&nbsp;MANCHESTER REVIEW&nbsp;• 2001&nbsp;•
                  VOLUME&nbsp;6&nbsp;• NUMBER&nbsp;1)
                </span>
              </li>

              <li>
                <a
                  href={
                    "https://ava.vivanti.eu/article/1/the_top_priority_for_sales_leaders_improving_sales_enablement_and_training_in_pharma_with_ava"
                  }
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  https://ava.vivanti.eu/article/1/the_top_priority_for_sales_leaders_improving_sales_enablement_and_training_in_pharma_with_ava.
                </a>
              </li>
            </ol>
          </RootDesc>
        </div>
      </Paragraph>
    </>
  );
};
